import React from "react";
import { Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import StatusBadge from "./StatusBadge";
import TSASection from "./TSASection";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "long", timeStyle: "short" }); } catch { return iso; }
}

function Row({ label, value, mono, dataTestid }) {
  if (!value) return null;
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-3 border-b" style={{ borderColor: "var(--vault-border)" }}>
      <p className="overline text-[var(--vault-text-muted)] sm:w-40 flex-shrink-0">{label}</p>
      <p className={`text-sm break-all ${mono ? "font-mono-vault" : ""}`} data-testid={dataTestid}>{value}</p>
    </div>
  );
}

function ShaRow({ sha }) {
  const copy = () => {
    navigator.clipboard.writeText(sha);
    toast.success("Copied");
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-4 py-3 border-b" style={{ borderColor: "var(--vault-border)" }}>
      <p className="overline text-[var(--vault-text-muted)] sm:w-40 flex-shrink-0">SHA-256</p>
      <div className="flex-1 flex items-center gap-2 min-w-0">
        <p className="text-xs font-mono-vault break-all flex-1" data-testid="auth-sha256">{sha}</p>
        <button
          type="button"
          onClick={copy}
          className="p-1.5 rounded hover:bg-white/5 flex-shrink-0"
          title="Copy hash"
          data-testid="auth-copy-sha"
        >
          <Copy className="w-3.5 h-3.5 text-[var(--vault-text-muted)]" />
        </button>
      </div>
    </div>
  );
}

export default function ResultCard({ result }) {
  return (
    <div
      className="mt-10 rounded-2xl p-6 sm:p-8 fade-up delay-2"
      style={{ background: "var(--vault-surface)", border: "1px solid var(--vault-border)" }}
      data-testid="verify-result"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <p className="overline text-[var(--vault-text-muted)] mb-1">Result</p>
          <h2 className="font-display text-2xl">
            {result.valid ? "Certificate verified" : "Unknown code"}
          </h2>
        </div>
        <StatusBadge valid={result.valid} isActive={result.is_active} expiresAt={result.expires_at} />
      </div>

      {!result.valid && (
        <p className="text-sm text-[var(--vault-text-muted)]">
          {result.message || "No certificate matches this code. If you believe this is an error, contact us."}
        </p>
      )}

      {result.valid && (
        <div>
          <Row label="Code" value={result.code} mono dataTestid="auth-code" />
          <Row label="Label" value={result.label || "—"} dataTestid="auth-label" />
          <Row label="Issued by" value={result.brand_name} dataTestid="auth-brand" />
          <Row label="Issued on" value={fmtDate(result.issued_at)} dataTestid="auth-issued" />
          {result.expires_at && <Row label="Expires on" value={fmtDate(result.expires_at)} dataTestid="auth-expires" />}
          <Row label="Document" value={result.file_name || "—"} dataTestid="auth-filename" />
          {result.file_sha256 && <ShaRow sha={result.file_sha256} />}
          {typeof result.access_count === "number" && (
            <Row label="Views" value={String(result.access_count)} mono dataTestid="auth-views" />
          )}
          {result.last_accessed_at && (
            <Row label="Last viewed" value={fmtDate(result.last_accessed_at)} dataTestid="auth-last" />
          )}

          <div className="mt-6 p-4 rounded-lg text-xs leading-relaxed text-[var(--vault-text-muted)]" style={{ background: "var(--vault-bg)", border: "1px solid var(--vault-border)" }}>
            <p className="overline mb-2" style={{ color: "var(--vault-gold)" }}>How to verify independently</p>
            <ol className="list-decimal list-inside space-y-1.5">
              <li>Compute the SHA-256 of your certificate file (on Linux/Mac: <code className="font-mono-vault text-white">shasum -a 256 file.pdf</code>).</li>
              <li>Compare it to the hash shown above — they must match character-for-character.</li>
              <li>The "Issued on" date is the immutable record of when the code was minted in our vault.</li>
            </ol>
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <a href={`/?code=${encodeURIComponent(result.code)}`} className="btn-gold inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm" data-testid="auth-open-portal">
              Open the certificate <ExternalLink className="w-3.5 h-3.5" />
            </a>
          </div>

          <TSASection result={result} />
        </div>
      )}
    </div>
  );
}
